<template>
  <div class="container-chat-layout">
    <div class="container-chat" ref="containerRef" :style="{ width: chatWidth + 'px' }">
      <ChatHeader domain="CHAT" :type-call="callType" is-menu-open />

      <ChatLoading v-if="loading" />
      <template v-else>
        <router-view v-slot="{ Component }">
          <component :is="Component" />
        </router-view>
      </template>
    </div>

    <!--보조화면 원격 제어-->
    <div class="container-settings" :style="{ width: secretWidth + 'px' }" v-show="isVisibleAuxiliaryDisplayControl">
      <AuxiliaryDisplayControl />
    </div>

    <!--MCS-->
    <template v-if="chatUI.state.isVisibleMCSPController || chatUI.state.isVisibleMCSController">
      <div class="resizer">
        <button
          class="button-glass-dark button-icon button-icon-small button-rounded resizer-handle"
          @mousedown="startResize"
        >
          <IconChange class="icon-small" />
        </button>
      </div>

      <div class="container-secret" :style="{ width: secretWidth + 'px' }">
        <MCSControllerBoard v-if="chatUI.state.isVisibleMCSPController" />
        <SMCSControllerBoard v-if="chatUI.state.isVisibleMCSController" />
      </div>
    </template>

    <SecretController v-if="isVisibleSecretLangaugeChange" />
    <SecretMicController v-if="chatUI.state.isVisibleMicSecretController" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, provide, readonly, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import AuxiliaryDisplayControl from '@/components/domains/chat/chat-general/AuxiliaryDisplayControl.vue';
import SecretController from '@/components/domains/chat/chat-general/SecretController.vue';
import SecretMicController from '@/components/domains/chat/chat-general/SecretMicController.vue';
import ChatHeader from '@/components/domains/chat/common/ChatHeader.vue';
import ChatLoading from '@/components/domains/chat/common/ChatLoading.vue';
import MCSControllerBoard from '@/components/domains/chat/mcs/MCSControllerBoard.vue';
import SMCSControllerBoard from '@/components/domains/chat/mcs/SMCSControllerBoard.vue';
import IconChange from '@/components/icons/IconChange.vue';
import { useChatUI } from '@/composables/domains/chat/useChatUI';
import useSize from '@/composables/shared/useSize';
import { useSocketController } from '@/composables/shared/useSocketController';
import { useViewportHeight } from '@/composables/shared/useViewportHeight';
import { useAudioStore } from '@/stores/audio';
import { useCallStore } from '@/stores/call';
import { useSocketStore } from '@/stores/socket';
import { useUserStore } from '@/stores/user';
import { TypeCall } from '@/types/common';
import { CHAT_TYPES } from '@/utils/constants/chat';
import {
  ij_bottomBarStatus,
  ij_chat_mode,
  ij_chat_ui,
  ij_chatId,
  ij_closeAuxiliaryDisplayControl,
  ij_isVisibleAuxiliaryDisplayControl,
  ij_isVisibleButtonMyLanguage,
  ij_isVisibleSecretController,
  ij_isVisibleSecretLangaugeChange,
  ij_openAuxiliaryDisplayControl,
  ij_setBottomBarStatus,
  ij_toggleIsVisibleSecretController,
  ij_toggleIsVisibleSecretLangaugeChange,
  ij_domain,
} from '@/utils/injectionKeys';

const MAX_CHAT_WIDTH: Readonly<number> = 1400; // 채팅 화면 최대 넓이

const socketStore = useSocketStore();
const audioStore = useAudioStore();
const userStore = useUserStore();
const callStore = useCallStore();

const route = useRoute();

const { width: chatWidth } = useSize('containerRef');
const { width: secretWidth } = useSize('secretContainerRef');

/**
 * TODO - ChatUI 관련 상태 통합 진행 중입니다.
 * - 아래 상태들은 점진적으로 ChatUI 객체로 이전될 예정입니다.
 * 대상 상태:
 * 1) isVisibleButtonMyLanguage
 * 2) isVisibleSecretController
 * 3) isVisibleSecretLangaugeChange
 * */
const chatUI = useChatUI();
const isVisibleSecretController = ref<boolean>(false); // "멀티 채널 지원 보기" 노출 여부
const isVisibleButtonMyLanguage = ref<boolean>(true); // "나의 언어만 보기" 노출 여부
const isVisibleSecretLangaugeChange = ref<boolean>(false); // "테스터 전용 컨트롤러" 노출 여부
const _isVisibleAuxiliaryDisplayControl = ref<boolean>(false); // 보조화면 노출 여부
const isVisibleAuxiliaryDisplayControl = readonly(_isVisibleAuxiliaryDisplayControl);

const isResizing = ref<boolean>(false);

// 현재 라우트의 meta 속성 중, chatType을 반환합니다.
const callType = computed<TypeCall>(() => {
  const { chatType = 'GENERAL' } = route.meta;
  return Object.keys(CHAT_TYPES).includes(chatType) ? chatType : 'GENERAL';
});

const code = computed(() => {
  return ['AUDIENCE'].includes(callType.value) ? userStore.userId : route.params.code;
});

const isUsingDirectMessage = computed<boolean>(() => {
  return callStore.qrCodeInfo.options?.directMessage;
});

const savePartnerCode = () => {
  const partnerCode = (route.params.partnerCode || '').toString();
  if (partnerCode) {
    localStorage.setItem('partner-code-admin', partnerCode);
  }
};

const {
  loading, // 로딩
  bottomBarStatus, // bottomBarStatus
  setBottomBarStatus, //
  setupWebsocket, // 웹소켓 세팅
  connectWebsocket, // 웹소켓 연결
  disconnectWebsocket, // 웹소켓 연결끊기
} = useSocketController(code, callType, 'CHAT');

const toggleIsVisibleSecretController = () => {
  isVisibleSecretController.value = !isVisibleSecretController.value;
};

const toggleIsVisibleSecretLanguageChange = () => {
  isVisibleSecretLangaugeChange.value = !isVisibleSecretLangaugeChange.value;
};

const openAuxiliaryDisplayControl = () => {
  _isVisibleAuxiliaryDisplayControl.value = true;
};

const closeAuxiliaryDisplayControl = () => {
  _isVisibleAuxiliaryDisplayControl.value = false;
};

const startResize = () => {
  isResizing.value = true;
  document.addEventListener('mousemove', resize);
  document.addEventListener('mouseup', stopResize);
};

const resize = (e?: MouseEvent) => {
  if (!isResizing.value) return;

  const minChat = 360;
  const minSecret = 250;

  const width = e ? e.clientX : MAX_CHAT_WIDTH;
  const newChat = Math.max(Math.min(width, MAX_CHAT_WIDTH), minChat);
  const newSecret = window.innerWidth - newChat;

  if (newSecret >= minSecret) {
    chatWidth.value = newChat;
    secretWidth.value = newSecret;
  }
};

const stopResize = () => {
  isResizing.value = false;
  document.removeEventListener('mousemove', resize);
  document.removeEventListener('mouseup', stopResize);
};

// iOS 키보드 대응 및 viewport height 계산
useViewportHeight();

onMounted(async () => {
  savePartnerCode();
  await audioStore.fetchLanguageOptions();
  if (socketStore.socketState !== 'OPEN') {
    await setupWebsocket();
    await connectWebsocket(true);
  }
});

onUnmounted(async () => {
  socketStore.sendMessageAudioStreamEnd();
  disconnectWebsocket();
});

watch(chatWidth, (val) => {
  if (isVisibleSecretController.value && val > MAX_CHAT_WIDTH) {
    chatWidth.value = MAX_CHAT_WIDTH;
    secretWidth.value = window.innerWidth - chatWidth.value;
  }
});

// "나의 언어만 보기" 버튼 노출 여부를 결정합니다.
watch(
  () => audioStore.isSingleLanguage,
  (newVal) => {
    if (!isUsingDirectMessage.value) {
      isVisibleButtonMyLanguage.value = !newVal;
    }
  },
  { immediate: true }
);

provide(ij_chatId, socketStore.selectedChatId);
provide(ij_bottomBarStatus, bottomBarStatus);
provide(ij_setBottomBarStatus, setBottomBarStatus);
provide(ij_chat_mode, 'DEFAULT');
provide(ij_isVisibleAuxiliaryDisplayControl, isVisibleAuxiliaryDisplayControl);
provide(ij_openAuxiliaryDisplayControl, openAuxiliaryDisplayControl);
provide(ij_closeAuxiliaryDisplayControl, closeAuxiliaryDisplayControl);

/**
 * TODO - ChatUI 관련 상태 통합 진행 중입니다.
 * - 아래 상태들은 점진적으로 ChatUI 객체로 이전될 예정입니다.
 * 대상 상태:
 * 1) isVisibleButtonMyLanguage
 * 2) isVisibleSecretController
 * 3) isVisibleSecretLangaugeChange
 * */
provide(ij_chat_ui, chatUI);
provide(ij_isVisibleButtonMyLanguage, isVisibleButtonMyLanguage);
provide(ij_isVisibleSecretController, isVisibleSecretController);
provide(ij_toggleIsVisibleSecretController, toggleIsVisibleSecretController);
provide(ij_isVisibleSecretLangaugeChange, isVisibleSecretLangaugeChange);
provide(ij_toggleIsVisibleSecretLangaugeChange, toggleIsVisibleSecretLanguageChange);
provide(ij_domain, 'CHAT');
</script>

<style scoped lang="scss">
$visual-height: var(--vh, 100dvh);

.container-chat-layout {
  display: flex;
  width: 100%;
  height: $visual-height;
  max-height: $visual-height;
  background: var(--background-theme);
  overflow: hidden;
  overscroll-behavior: none;
}

.container-chat {
  width: 100%;
  height: $visual-height;
  max-height: $visual-height;
  min-width: 22.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  flex-shrink: 0;
  overflow: hidden;
}

.container-secret {
  width: 100%;
  height: 100%;
  min-width: 60rem;
  overflow-y: auto;
}

.container-settings {
  width: 100%;
  height: 100%;
  min-width: 30rem;
  overflow-y: auto;
  border-left: solid 1px var(--border-dim);
}

.resizer {
  width: 0.125rem;
  height: 100%;
  cursor: col-resize;
  background: var(--border-dark);
  position: relative;
  z-index: $z-floating;
}

.resizer-handle {
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  cursor: col-resize;
  z-index: $z-floating;
}
</style>
